import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, Clock, FileText, Settings, Plane, Building2, Search, Filter } from 'lucide-react';
import api from '../../services/api';
import ItineraryViewModal from './ItineraryViewModal';

interface BookedItinerary {
  itinerary_id: number;
  booking_id: string;
  lead_guest_name: string;
  itinerary_name: string;
  agency_name: string;
  travel_date: string;
  no_of_days: number;
  no_of_pax: number;
  no_of_children: number;
  trip_status: string;
  operation_cost_status: string;
  given_price: number;
  currency: string;
}

const ConvertedItineraryPage: React.FC = () => {
  const [itineraries, setItineraries] = useState<BookedItinerary[]>([]);
  const [activeTab, setActiveTab] = useState<string>('ongoing');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedItineraryId, setSelectedItineraryId] = useState<number | null>(null);
  const [selectedAgencyName, setSelectedAgencyName] = useState<string>('');
  const [syncing, setSyncing] = useState(false);
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchItineraries();
  }, []);

  const fetchItineraries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/converted-itineraries');
      if (response.data.success) {
        setItineraries(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch confirmed trips');
      console.error('Error fetching itineraries:', err);
    } finally {
      setLoading(false);
    }
  };

  const syncHotelAddresses = async () => {
    try {
      setSyncing(true);
      const response = await api.post('/converted-itineraries/sync-hotel-addresses');
      if (response.status === 200) {
        alert(`Successfully synced ${response.data.updated_count} hotel addresses from database`);
        // Refresh the modal data if it's open
        if (showViewModal && selectedItineraryId) {
          // Force refresh of the modal data
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Error syncing hotel addresses:', error);
      alert('Failed to sync hotel addresses');
    } finally {
      setSyncing(false);
    }
  };

  const getFilteredItineraries = (status: string) => {
    const today = new Date();
    let filtered = itineraries.filter(itinerary => {
      const travelDate = new Date(itinerary.travel_date);
      const endDate = new Date(travelDate.getTime() + (itinerary.no_of_days * 24 * 60 * 60 * 1000));
      
      switch (status) {
        case 'ongoing':
          return travelDate <= today && endDate >= today;
        case 'upcoming':
          return travelDate > today;
        case 'completed':
          return endDate < today;
        default:
          return true;
      }
    });
    
    // Apply search filters
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(itinerary => {
        const itineraryId = `WH_${itinerary.itinerary_id}`.toLowerCase();
        const itineraryIdWithoutPrefix = itinerary.itinerary_id.toString();
        return (
          itineraryId.includes(query) ||
          itineraryIdWithoutPrefix.includes(query) ||
          itinerary.lead_guest_name?.toLowerCase().includes(query) ||
          itinerary.agency_name?.toLowerCase().includes(query) ||
          itinerary.itinerary_name?.toLowerCase().includes(query)
        );
      });
    }
    
    // Apply agency filter
    if (agencyFilter.trim()) {
      const query = agencyFilter.toLowerCase();
      filtered = filtered.filter(itinerary => 
        itinerary.agency_name?.toLowerCase().includes(query)
      );
    }
    
    // Apply date filters
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      filtered = filtered.filter(itinerary => new Date(itinerary.travel_date) >= fromDate);
    }
    
    if (dateTo) {
      const toDate = new Date(dateTo);
      filtered = filtered.filter(itinerary => new Date(itinerary.travel_date) <= toDate);
    }
    
    return filtered;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Not Completed':
        return 'bg-yellow-100 text-yellow-800';
      case 'Cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getOperationStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'INPROGRESS':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const ongoingTrips = getFilteredItineraries('ongoing');
  const upcomingTrips = getFilteredItineraries('upcoming');
  const completedTrips = getFilteredItineraries('completed');

  const tabs = [
    { id: 'ongoing', label: `Ongoing Trips (${ongoingTrips.length})`, data: ongoingTrips },
    { id: 'upcoming', label: `Upcoming Trips (${upcomingTrips.length})`, data: upcomingTrips },
    { id: 'completed', label: `Completed Trips (${completedTrips.length})`, data: completedTrips }
  ];

  const activeTabData = tabs.find(tab => tab.id === activeTab)?.data || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error</div>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <div className="w-full px-3 sm:px-6 lg:px-8 py-3 sm:py-6 flex-1 overflow-y-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Confirmed Trips</h1>
              <p className="mt-1 text-xs sm:text-sm text-gray-500">Manage and view all confirmed itineraries</p>
            </div>
            <button
              onClick={syncHotelAddresses}
              disabled={syncing}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
            >
              {syncing ? 'Syncing...' : 'Sync Hotel Addresses'}
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-4 sm:mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
          <div className="flex flex-col gap-3 sm:gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <input
                  type="text"
                  placeholder="Search by Itinerary ID, Guest Name, Agency..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            
            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 border rounded-lg transition-colors text-sm ${
                showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
          </div>
          
          {/* Date Filters */}
          {showFilters && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Travel Date From
                  </label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      if (e.target.value && !dateTo) {
                        setDateTo(e.target.value);
                      }
                    }}
                    className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Travel Date To
                  </label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Agency Name
                  </label>
                  <input
                    type="text"
                    placeholder="Filter by agency..."
                    value={agencyFilter}
                    onChange={(e) => setAgencyFilter(e.target.value)}
                    className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>
              
              {/* Clear Filters */}
              {(searchQuery || dateFrom || dateTo || agencyFilter) && (
                <div className="mt-3 sm:mt-4">
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setDateFrom('');
                      setDateTo('');
                      setAgencyFilter('');
                    }}
                    className="text-xs sm:text-sm text-blue-600 hover:text-blue-800"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 min-h-0">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-4 sm:space-x-6 lg:space-x-8 px-3 sm:px-6 overflow-x-auto" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="sm:hidden">
                    {tab.label.split(' ')[0]} ({tab.data.length})
                  </span>
                  <span className="hidden sm:inline">
                    {tab.label}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Mobile Card View (hidden on desktop) */}
          <div className="block sm:hidden h-full">
            <div className="p-2 space-y-2 h-full overflow-y-auto overflow-x-hidden">
              {activeTabData.map((itinerary) => (
                <div key={itinerary.itinerary_id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm w-full min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center min-w-0 flex-1">
                      <FileText className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 break-words">
                          WH_{itinerary.itinerary_id}
                        </div>
                        <div className="text-xs text-gray-500 truncate" title={itinerary.itinerary_name}>
                          {(itinerary.itinerary_name || '').length > 30 ? `${itinerary.itinerary_name.substring(0, 30)}...` : itinerary.itinerary_name}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                      <button 
                        className="text-blue-600 hover:text-blue-900 p-1 rounded" 
                        title="Configure Itinerary"
                        onClick={() => {
                          setSelectedItineraryId(itinerary.itinerary_id);
                          setSelectedAgencyName(itinerary.agency_name);
                          setShowViewModal(true);
                        }}
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                    <div className="min-w-0">
                      <div className="text-gray-500">Guest:</div>
                      <div className="font-medium truncate" title={itinerary.lead_guest_name || 'No guest'}>
                        {(itinerary.lead_guest_name || 'No guest').length > 15 ? `${(itinerary.lead_guest_name || 'No guest').substring(0, 15)}...` : (itinerary.lead_guest_name || 'No guest')}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="text-gray-500">Agency:</div>
                      <div className="font-medium truncate" title={itinerary.agency_name || 'No agency'}>
                        {(itinerary.agency_name || 'No agency').length > 15 ? `${(itinerary.agency_name || 'No agency').substring(0, 15)}...` : (itinerary.agency_name || 'No agency')}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="text-gray-500">Travel Date:</div>
                      <div className="font-medium break-words">
                        {new Date(itinerary.travel_date).toLocaleDateString('en-GB')}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="text-gray-500">Duration & Pax:</div>
                      <div className="font-medium break-words">
                        {itinerary.no_of_days}d • {itinerary.no_of_pax}p
                        {itinerary.no_of_children > 0 && ` +${itinerary.no_of_children}c`}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center flex-wrap gap-1">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full flex-shrink-0 ${getStatusColor(itinerary.trip_status)}`}>
                      {itinerary.trip_status}
                    </span>
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full flex-shrink-0 ${getOperationStatusColor(itinerary.operation_cost_status)}`}>
                      {itinerary.operation_cost_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Table View (hidden on mobile) */}
          <div className="hidden sm:block h-full">
            <div className="overflow-x-auto h-full">
              <table className="min-w-full divide-y divide-gray-200 h-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      Trip Details
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      Guest & Agency
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      Travel Info
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activeTabData.map((itinerary) => (
                    <tr key={itinerary.itinerary_id} className="hover:bg-gray-50">
                      <td className="px-3 py-4">
                        <div className="flex items-center">
                          <FileText className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              WH_{itinerary.itinerary_id}
                            </div>
                            <div className="text-sm text-gray-500 truncate" title={itinerary.itinerary_name}>
                              {(itinerary.itinerary_name || 'Untitled').length > 25 
                                ? `${(itinerary.itinerary_name || 'Untitled').substring(0, 25)}...` 
                                : (itinerary.itinerary_name || 'Untitled')
                              }
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-3 py-4">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate" title={itinerary.lead_guest_name}>
                            {(itinerary.lead_guest_name || 'No guest').length > 20 
                              ? `${(itinerary.lead_guest_name || 'No guest').substring(0, 20)}...` 
                              : (itinerary.lead_guest_name || 'No guest')
                            }
                          </div>
                          <div className="text-sm text-gray-500 truncate" title={itinerary.agency_name}>
                            {(itinerary.agency_name || 'No agency').length > 20 
                              ? `${(itinerary.agency_name || 'No agency').substring(0, 20)}...` 
                              : (itinerary.agency_name || 'No agency')
                            }
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-3 py-4">
                        <div className="text-sm text-gray-900 flex items-center">
                          <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{new Date(itinerary.travel_date).toLocaleDateString('en-GB')}</span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1 flex items-center">
                          <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span>{itinerary.no_of_days} days</span>
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Users className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span>
                            {itinerary.no_of_pax} pax
                            {itinerary.no_of_children > 0 && ` +${itinerary.no_of_children}c`}
                          </span>
                        </div>
                      </td>
                      
                      <td className="px-3 py-4 text-sm font-medium">
                        <div className="flex items-center justify-center">
                          <button 
                            className="text-blue-600 hover:text-blue-900 p-1 rounded" 
                            title="Configure Itinerary"
                            onClick={() => {
                              setSelectedItineraryId(itinerary.itinerary_id);
                              setSelectedAgencyName(itinerary.agency_name);
                              setShowViewModal(true);
                            }}
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {activeTabData.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No trips found in this category.</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal */}
      {showViewModal && selectedItineraryId && (
        <ItineraryViewModal
          itineraryId={selectedItineraryId}
          agencyName={selectedAgencyName}
          onClose={() => {
            setShowViewModal(false);
            setSelectedItineraryId(null);
            setSelectedAgencyName('');
          }}
        />
      )}
    </div>
  );
};

export default ConvertedItineraryPage;